/**
 * WhatsApp Flow definition model + helpers, shared by the visual editor, the phone
 * preview, and the detail/export. The "definition" (screens) is our own beginner-
 * friendly model; `generateFlowJson` turns it into the Meta Flow JSON used for
 * publishing. No JSON is required from the user — they build screens visually.
 */

export type FieldType =
  | 'TextHeading' | 'TextSubheading' | 'TextBody'
  | 'TextInput' | 'TextArea' | 'Dropdown' | 'RadioButtonsGroup' | 'DatePicker' | 'OptIn';

export interface FlowField {
  id: string;
  type: FieldType;
  text: string;     // display components
  label: string;    // input components
  name: string;     // input field key
  required: boolean;
  options: string;  // comma-separated (Dropdown / RadioButtonsGroup)
}

export interface FlowScreen {
  id: string;
  title: string;
  fields: FlowField[];
  footerLabel: string;
  action: 'navigate' | 'complete';
  nextId: string | null;
}

export const DISPLAY_TYPES: FieldType[] = ['TextHeading', 'TextSubheading', 'TextBody'];
export const INPUT_TYPES: FieldType[] = ['TextInput', 'TextArea', 'Dropdown', 'RadioButtonsGroup', 'DatePicker', 'OptIn'];

export interface FieldSpec {
  value: FieldType;
  label: string;
  group: 'Text' | 'Question';
  hint: string;
  example: string;
}

/** Friendly catalog with plain-language hints + examples (powers the "+ Add" menu). */
export const FIELD_CATALOG: FieldSpec[] = [
  { value: 'TextHeading', label: 'Heading', group: 'Text', hint: 'A big title at the top of the screen.', example: 'Welcome!' },
  { value: 'TextSubheading', label: 'Subheading', group: 'Text', hint: 'A smaller title under the heading.', example: 'Tell us about yourself' },
  { value: 'TextBody', label: 'Paragraph', group: 'Text', hint: 'A line or two of explanation.', example: 'It only takes a minute.' },
  { value: 'TextInput', label: 'Short answer', group: 'Question', hint: 'A single-line text box (name, email…).', example: 'Full name' },
  { value: 'TextArea', label: 'Long answer', group: 'Question', hint: 'A multi-line text box for longer replies.', example: 'Your message' },
  { value: 'Dropdown', label: 'Dropdown', group: 'Question', hint: 'Pick one from a list. Add options below.', example: 'Choose a service' },
  { value: 'RadioButtonsGroup', label: 'Multiple choice', group: 'Question', hint: 'Pick one with radio buttons. Add options below.', example: 'How was your visit?' },
  { value: 'DatePicker', label: 'Date', group: 'Question', hint: 'Let them pick a date.', example: 'Preferred date' },
  { value: 'OptIn', label: 'Checkbox / consent', group: 'Question', hint: 'A tick box, e.g. agree to terms.', example: 'I agree to be contacted' },
];

export const FLOW_CATEGORIES = [
  'SIGN_UP', 'SIGN_IN', 'APPOINTMENT_BOOKING', 'LEAD_GENERATION',
  'CONTACT_US', 'CUSTOMER_SUPPORT', 'SURVEY', 'OTHER',
] as const;

export const isInput = (t: FieldType) => INPUT_TYPES.includes(t);
export const isDisplay = (t: FieldType) => DISPLAY_TYPES.includes(t);
export const fieldLabel = (t: FieldType) => FIELD_CATALOG.find((f) => f.value === t)?.label ?? t;

let counter = 0;
const uid = (prefix: string) => `${prefix}_${counter++}_${Math.round(Math.random() * 1e6).toString(36)}`;

export function newField(type: FieldType, index: number): FlowField {
  return { id: uid('f'), type, text: '', label: '', name: `field_${index + 1}`, required: false, options: '' };
}

export function newScreen(index = 0): FlowScreen {
  return { id: uid('s'), title: `Screen ${index + 1}`, fields: [], footerLabel: 'Continue', action: 'complete', nextId: null };
}

function screenName(s: FlowScreen, index: number): string {
  const base = (s.title || `SCREEN_${index + 1}`).toUpperCase().replace(/[^A-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return base || `SCREEN_${index + 1}`;
}

/** Generate the Meta Flow JSON (v5.0) from the visual screens model. */
export function generateFlowJson(screens: FlowScreen[]): string {
  const names = screens.map((s, i) => screenName(s, i));
  const out = {
    version: '5.0',
    screens: screens.map((s, i) => {
      const display = s.fields.filter((f) => isDisplay(f.type)).map((f) => ({ type: f.type, text: f.text || ' ' }));
      const inputs = s.fields.filter((f) => isInput(f.type)).map((f) => {
        const base: Record<string, unknown> = { type: f.type, label: f.label || f.name, name: f.name, required: f.required };
        if (f.type === 'Dropdown' || f.type === 'RadioButtonsGroup') {
          base['data-source'] = f.options.split(',').map((o) => o.trim()).filter(Boolean).map((o, idx) => ({ id: `${idx}`, title: o }));
        }
        if (f.type === 'TextInput') base['input-type'] = 'text';
        return base;
      });

      const nextIdx = s.nextId ? screens.findIndex((x) => x.id === s.nextId) : -1;
      const footer = {
        type: 'Footer',
        label: s.footerLabel || 'Continue',
        'on-click-action':
          s.action === 'navigate' && nextIdx >= 0
            ? { name: 'navigate', next: { type: 'screen', name: names[nextIdx] }, payload: {} }
            : { name: 'complete', payload: Object.fromEntries(inputs.map((inp) => [inp.name as string, `\${form.${inp.name}}`])) },
      };

      const children = inputs.length > 0
        ? [...display, { type: 'Form', name: 'form', children: [...inputs, footer] }]
        : [...display, footer];

      return {
        id: names[i],
        title: s.title || `Screen ${i + 1}`,
        terminal: s.action === 'complete',
        success: s.action === 'complete' ? true : undefined,
        data: {},
        layout: { type: 'SingleColumnLayout', children },
      };
    }),
  };
  return JSON.stringify(out, null, 2);
}

/** Serialize/parse the visual definition stored on the flow record. */
export function serializeDefinition(screens: FlowScreen[]): string {
  return JSON.stringify({ screens });
}

export function parseDefinition(definition?: string | null): FlowScreen[] {
  if (!definition) return [newScreen(0)];
  try {
    const parsed = JSON.parse(definition);
    const screens = Array.isArray(parsed?.screens) ? parsed.screens : Array.isArray(parsed) ? parsed : null;
    if (!screens || screens.length === 0) return [newScreen(0)];
    // Re-key ids so the editor's dnd/state stays stable and unique.
    return screens.map((s: any, i: number) => ({
      id: uid('s'),
      title: String(s.title ?? `Screen ${i + 1}`),
      footerLabel: String(s.footerLabel ?? 'Continue'),
      action: s.action === 'navigate' ? 'navigate' : 'complete',
      nextId: null, // re-linked below
      fields: Array.isArray(s.fields) ? s.fields.map((f: any, j: number) => ({
        id: uid('f'),
        type: (FIELD_CATALOG.find((c) => c.value === f.type)?.value ?? 'TextBody') as FieldType,
        text: String(f.text ?? ''),
        label: String(f.label ?? ''),
        name: String(f.name ?? `field_${j + 1}`),
        required: !!f.required,
        options: String(f.options ?? ''),
      })) : [],
    })) as FlowScreen[];
  } catch {
    return [newScreen(0)];
  }
}

// ---- Starter templates (beginner-friendly presets) -------------------------
export interface FlowTemplate {
  key: string;
  name: string;
  description: string;
  category: (typeof FLOW_CATEGORIES)[number];
  build: () => FlowScreen[];
}

function screenWith(title: string, footerLabel: string, fields: Omit<FlowField, 'id'>[]): FlowScreen {
  return { ...newScreen(0), title, footerLabel, action: 'complete', nextId: null, fields: fields.map((f) => ({ ...f, id: uid('f') })) };
}

export const FLOW_TEMPLATES: FlowTemplate[] = [
  {
    key: 'signup', name: 'Sign-up form', description: 'Collect a name and email to register a contact.', category: 'SIGN_UP',
    build: () => [screenWith('Sign up', 'Submit', [
      { type: 'TextHeading', text: 'Join us', label: '', name: '', required: false, options: '' },
      { type: 'TextInput', text: '', label: 'Full name', name: 'full_name', required: true, options: '' },
      { type: 'TextInput', text: '', label: 'Email', name: 'email', required: true, options: '' },
    ])],
  },
  {
    key: 'appointment', name: 'Appointment booking', description: 'Let people pick a service and date.', category: 'APPOINTMENT_BOOKING',
    build: () => [screenWith('Book an appointment', 'Book', [
      { type: 'TextHeading', text: 'Book an appointment', label: '', name: '', required: false, options: '' },
      { type: 'TextInput', text: '', label: 'Your name', name: 'full_name', required: true, options: '' },
      { type: 'Dropdown', text: '', label: 'Service', name: 'service', required: true, options: 'Consultation, Counselling, Prayer' },
      { type: 'DatePicker', text: '', label: 'Preferred date', name: 'date', required: true, options: '' },
    ])],
  },
  {
    key: 'feedback', name: 'Feedback survey', description: 'Ask for a rating and comments.', category: 'SURVEY',
    build: () => [screenWith('Feedback', 'Send', [
      { type: 'TextHeading', text: 'We value your feedback', label: '', name: '', required: false, options: '' },
      { type: 'RadioButtonsGroup', text: '', label: 'How was your experience?', name: 'rating', required: true, options: 'Excellent, Good, Okay, Poor' },
      { type: 'TextArea', text: '', label: 'Any comments?', name: 'comments', required: false, options: '' },
    ])],
  },
  {
    key: 'contact', name: 'Contact us', description: 'A simple contact / enquiry form.', category: 'CONTACT_US',
    build: () => [screenWith('Contact us', 'Send', [
      { type: 'TextHeading', text: 'Get in touch', label: '', name: '', required: false, options: '' },
      { type: 'TextInput', text: '', label: 'Name', name: 'full_name', required: true, options: '' },
      { type: 'TextInput', text: '', label: 'Email or phone', name: 'contact', required: true, options: '' },
      { type: 'TextArea', text: '', label: 'How can we help?', name: 'message', required: true, options: '' },
    ])],
  },
];
